import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import axios from 'axios';
import { Shield, Building2, Database, Save, RefreshCw, Check, X, AlertCircle, Search } from 'lucide-react';

const EntityEntitlementManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('tenant'); // 'tenant' or 'entity'
  
  // Tenant view state
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenantEntitlements, setTenantEntitlements] = useState([]);
  const [allEntities, setAllEntities] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  
  // Entity view state
  const [selectedEntity, setSelectedEntity] = useState('');
  const [entityTenants, setEntityTenants] = useState([]);
  const [entitySearch, setEntitySearch] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'tenant' && selectedTenant) {
      loadTenantEntitlements(selectedTenant);
    } else if (activeTab === 'entity' && selectedEntity) {
      loadEntityTenants(selectedEntity);
    }
  }, [activeTab, selectedTenant, selectedEntity]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load tenants
      const tenantsResponse = await axios.get('/api/tenants');
      setTenants(tenantsResponse.data.tenants || []);

      // Load all entities from entity configs
      try {
        const entitiesResponse = await axios.get('/api/admin/entity-entitlements/available-entities');
        const entities = entitiesResponse.data.entities || [];
        setAllEntities(entities);
      } catch (err) {
        console.error('Error loading entities from config:', err);
        // Fallback to hardcoded list if API fails
        const fallbackEntities = [
          { type: 'Customer', displayName: 'Customer' },
          { type: 'Lead', displayName: 'Lead' },
          { type: 'Opportunity', displayName: 'Opportunity' },
          { type: 'Activity', displayName: 'Activity' },
          { type: 'SaleNote', displayName: 'Note' },
          { type: 'Note', displayName: 'Note' },
          { type: 'Company', displayName: 'Company' },
          { type: 'Contract', displayName: 'Contract' },
          { type: 'Invoice', displayName: 'Invoice' },
          { type: 'SupportTicket', displayName: 'Support Ticket' },
          { type: 'BusinessCard', displayName: 'Business Card' }
        ];
        setAllEntities(fallbackEntities);
        setError('Failed to load entities from config, using fallback list');
      }

      // Load all entitlements to get initial state
      const entitlementsResponse = await axios.get('/api/admin/entity-entitlements');
      const allEntitlements = entitlementsResponse.data.entitlements || [];
      
      if (allEntitlements.length > 0 && !selectedTenant) {
        // Auto-select first tenant if available
        const firstTenant = tenantsResponse.data.tenants?.[0];
        if (firstTenant) {
          setSelectedTenant(firstTenant.code);
        }
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantEntitlements = async (tenantCode) => {
    try {
      setError('');
      const response = await axios.get(`/api/admin/entity-entitlements/tenant/${tenantCode}`);
      const entitlements = response.data.entitlements || [];
      
      // Create a map of entitled entities
      const entitledSet = new Set(entitlements.filter(e => e.IsEnabled).map(e => e.EntityType));
      
      // Mark all entities with entitlement status
      const entitiesWithStatus = allEntities.map(entity => ({
        ...entity,
        isEntitled: entitledSet.has(entity.type),
        entitlementId: entitlements.find(e => e.EntityType === entity.type)?.EntitlementID || null
      }));
      
      setTenantEntitlements(entitiesWithStatus);
    } catch (err) {
      console.error('Error loading tenant entitlements:', err);
      setError(err.response?.data?.message || 'Failed to load tenant entitlements');
    }
  };

  const loadEntityTenants = async (entityType) => {
    try {
      setError('');
      const response = await axios.get(`/api/admin/entity-entitlements/entity/${entityType}`);
      const tenants = response.data.tenants || [];
      
      // Get all tenants and mark entitlement status
      const tenantsWithStatus = tenants.map(tenant => ({
        ...tenant,
        isEntitled: true,
        entitlementId: tenant.EntitlementID
      }));
      
      // Also include tenants that don't have this entity
      const entitledTenantCodes = new Set(tenants.map(t => t.TenantCode));
      const allTenantsWithStatus = tenants.map(tenant => {
        if (entitledTenantCodes.has(tenant.code)) {
          return tenantsWithStatus.find(t => t.TenantCode === tenant.code);
        }
        return {
          ...tenant,
          isEntitled: false,
          entitlementId: null
        };
      });
      
      setEntityTenants(allTenantsWithStatus);
    } catch (err) {
      console.error('Error loading entity tenants:', err);
      setError(err.response?.data?.message || 'Failed to load entity tenants');
    }
  };

  const handleToggleEntitlement = async (tenantCode, entityType, isCurrentlyEntitled, entitlementId) => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      if (isCurrentlyEntitled) {
        // Revoke entitlement
        await axios.delete(`/api/admin/entity-entitlements/${entitlementId}`);
        setSuccessMessage(`Entity entitlement revoked: ${entityType} from ${tenantCode}`);
      } else {
        // Grant entitlement
        await axios.post('/api/admin/entity-entitlements', {
          tenantCode,
          entityType,
          notes: `Granted by ${user?.username || user?.email}`
        });
        setSuccessMessage(`Entity entitlement granted: ${entityType} to ${tenantCode}`);
      }

      // Reload data
      if (activeTab === 'tenant') {
        await loadTenantEntitlements(selectedTenant);
      } else {
        await loadEntityTenants(selectedEntity);
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling entitlement:', err);
      setError(err.response?.data?.message || 'Failed to update entitlement');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkGrant = async (tenantCode, entityTypes) => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      await axios.post('/api/admin/entity-entitlements/bulk', {
        tenantCode,
        entityTypes,
        action: 'grant',
        notes: `Bulk granted by ${user?.username || user?.email}`
      });

      setSuccessMessage(`Bulk granted ${entityTypes.length} entities to ${tenantCode}`);
      await loadTenantEntitlements(selectedTenant);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error bulk granting:', err);
      setError(err.response?.data?.message || 'Failed to bulk grant entitlements');
    } finally {
      setSaving(false);
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.name?.toLowerCase().includes(tenantSearch.toLowerCase()) ||
    t.code?.toLowerCase().includes(tenantSearch.toLowerCase())
  );

  const filteredEntities = allEntities.filter(e =>
    e.displayName?.toLowerCase().includes(entitySearch.toLowerCase()) ||
    e.type?.toLowerCase().includes(entitySearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Entity Entitlement Management</h1>
        </div>
        <p className="text-gray-600 mt-2">
          Manage which entities each tenant can access. Tenant admins can only configure data governance policies for entitled entities.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('tenant')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tenant'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            Tenant View
          </button>
          <button
            onClick={() => setActiveTab('entity')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'entity'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Database className="h-4 w-4 inline mr-2" />
            Entity View
          </button>
        </nav>
      </div>

      {/* Tenant View */}
      {activeTab === 'tenant' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-gray-700">Select Tenant:</label>
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">-- Select a tenant --</option>
                {filteredTenants.map(tenant => (
                  <option key={tenant.code} value={tenant.code}>
                    {tenant.name} ({tenant.code})
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={tenantSearch}
                  onChange={(e) => setTenantSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {selectedTenant && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Entity Entitlements</h3>
                  <button
                    onClick={() => handleBulkGrant(selectedTenant, allEntities.map(e => e.type))}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                  >
                    Grant All Entities
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tenantEntitlements.map(entity => (
                    <div
                      key={entity.type}
                      className={`p-4 border rounded-lg ${
                        entity.isEntitled
                          ? 'border-green-300 bg-green-50'
                          : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{entity.displayName}</h4>
                          <p className="text-sm text-gray-500">{entity.type}</p>
                        </div>
                        <button
                          onClick={() => handleToggleEntitlement(
                            selectedTenant,
                            entity.type,
                            entity.isEntitled,
                            entity.entitlementId
                          )}
                          disabled={saving}
                          className={`p-2 rounded-md ${
                            entity.isEntitled
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } disabled:opacity-50`}
                        >
                          {entity.isEntitled ? (
                            <X className="h-5 w-5" />
                          ) : (
                            <Check className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Entity View */}
      {activeTab === 'entity' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-gray-700">Select Entity:</label>
              <select
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">-- Select an entity --</option>
                {filteredEntities.map(entity => (
                  <option key={entity.type} value={entity.type}>
                    {entity.displayName} ({entity.type})
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search entities..."
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {selectedEntity && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tenants with Access</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {entityTenants.map(tenant => (
                    <div
                      key={tenant.code || tenant.TenantCode}
                      className={`p-4 border rounded-lg ${
                        tenant.isEntitled
                          ? 'border-green-300 bg-green-50'
                          : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {tenant.name || tenant.TenantName || tenant.code || tenant.TenantCode}
                          </h4>
                          <p className="text-sm text-gray-500">{tenant.code || tenant.TenantCode}</p>
                        </div>
                        <button
                          onClick={() => handleToggleEntitlement(
                            tenant.code || tenant.TenantCode,
                            selectedEntity,
                            tenant.isEntitled,
                            tenant.entitlementId
                          )}
                          disabled={saving}
                          className={`p-2 rounded-md ${
                            tenant.isEntitled
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } disabled:opacity-50`}
                        >
                          {tenant.isEntitled ? (
                            <X className="h-5 w-5" />
                          ) : (
                            <Check className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityEntitlementManager;

